import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { StediStatusView } from '../types';

export interface StediStatusListProps {
  statuses: StediStatusView[];
  /** Roster cells need payer context; a payer table already supplies it in the first column. */
  showPayerName?: boolean;
  ariaLabel: string;
  /** Keep roster rows compact until staff asks to inspect every source row. */
  collapsedItemCount?: number;
}

/** Exact, unclassified Stedi values with their transaction rail. */
export function StediStatusList({
  statuses,
  showPayerName = false,
  ariaLabel,
  collapsedItemCount,
}: StediStatusListProps) {
  const [expanded, setExpanded] = useState(false);
  if (statuses.length === 0) {
    return <Typography color="text.secondary">—</Typography>;
  }

  const isCollapsible =
    typeof collapsedItemCount === 'number' &&
    collapsedItemCount > 0 &&
    statuses.length > collapsedItemCount;
  const visibleStatuses =
    isCollapsible && !expanded ? statuses.slice(0, collapsedItemCount) : statuses;

  return (
    <Stack
      component="ul"
      spacing={0.75}
      aria-label={ariaLabel}
      sx={{ listStyle: 'none', p: 0, m: 0, minWidth: 0, whiteSpace: 'normal' }}
    >
      {visibleStatuses.map((item, index) => (
        <Box component="li" key={`${item.enrollmentId}-${index}`} sx={{ minWidth: 0 }}>
          {showPayerName && (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.35 }}>
              {item.payerName}
            </Typography>
          )}
          <Typography
            sx={{
              color: 'text.secondary',
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: 1.35,
              overflowWrap: 'anywhere',
            }}
          >
            {`${item.transactionType} · ${item.status}`}
          </Typography>
        </Box>
      ))}
      {isCollapsible && (
        <Box component="li">
          <Button size="small" onClick={() => setExpanded(value => !value)} sx={{ px: 0 }}>
            {expanded ? 'Show fewer' : `Show all ${statuses.length} statuses`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
